import { ChangeDetectorRef, Component, computed, inject, Input, OnInit, signal  } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';
import { environment } from '../../../../environments/environment';
import { PaginatorModule } from 'primeng/paginator';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { AuctionItemComponent } from "./auction-item/auction-item.component";
import { Select } from "primeng/select";
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { MyAuctionsService } from '../../../services/bidder-community/my-auctions/my-auctions.service';
import { IAuctionDTO } from '../../../models/IAuctionsDTO';
import { ProgressSpinner } from "primeng/progressspinner";
import { Card } from "primeng/card";
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService } from '../../../services/authentication/auth.service';
import { ScrollTopModule } from 'primeng/scrolltop';

@Component({
  selector: 'app-my-auctions',
  imports: [
    PaginatorModule, 
    CommonModule, 
    TableModule, 
    AuctionItemComponent, 
    Select, 
    FormsModule, 
    FaIconComponent, 
    ProgressSpinner, 
    Card,
    ScrollTopModule
  ],
  templateUrl: './my-auctions.component.html',
  styleUrl: './my-auctions.component.scss'
})
export class MyAuctionsComponent implements OnInit{

  faFilter = faFilter;

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly myAuctionsService = inject(MyAuctionsService);
  private readonly authService = inject(AuthService);

  authSubscription!: Subscription;

  isAuthenticated = this.oidcSecurityService.authenticated;

  paginatedData : IAuctionDTO[] = [];

  myAuctionsList = signal<IAuctionDTO[] | null>(null);

  constructor(private bidderCommunityMenuService: BidderCommunityMenuService, private cdr: ChangeDetectorRef){

  }

  sortFilters = [
    { label: 'Auction Name - Descending', value: 'NAME_DESC' },
    { label: 'Auction Name - Ascending', value: 'NAME_ASC' },
    { label: 'Org Name - Descending', value: 'ORG_DESC' },
    { label: 'Org Name - Ascending', value: 'ORG_ASC' },
    { label: 'Time Left - Ascending', value: 'TIMELEFT_ASC' },
    { label: 'Time Left - Descending', value: 'TIMELEFT_DESC' },
  ];

  selectedSortSignal = signal('TIMELEFT_ASC');

  sortedItems = computed(() => {
    const sortValue = this.selectedSortSignal();
    return this.myAuctionsList()!.sort((a, b) => {

      switch (sortValue) {
        case 'NAME_ASC':
          return a.auctionName.localeCompare(b.auctionName);
        case 'NAME_DESC':
          return b.auctionName.localeCompare(a.auctionName);

        case 'ORG_ASC':
          return a.orgName.localeCompare(b.orgName);
        case 'ORG_DESC':
          return b.orgName.localeCompare(a.orgName);

        case 'TIMELEFT_DESC':
          {
            const now = new Date();
            const aClosed = new Date(a.onlineCloseDate) < now;
            const bClosed = new Date(b.onlineCloseDate) < now;

            if (aClosed !== bClosed) {
              return aClosed ? -1 : 1;
            }

            const diff = new Date(b.onlineCloseDate).getTime() - new Date(a.onlineCloseDate).getTime();
            if (diff !== 0) return diff;

            return b.auctionName.localeCompare(a.auctionName);
          }
        case 'TIMELEFT_ASC':
        default: {
          const now = new Date();
          const aClosed = new Date(a.onlineCloseDate) < now;
          const bClosed = new Date(b.onlineCloseDate) < now;

          if (aClosed !== bClosed) {
            return aClosed ? 1 : -1;
          }

          const diff = new Date(a.onlineCloseDate).getTime() - new Date(b.onlineCloseDate).getTime();
          if (diff !== 0) return diff;

          return a.auctionName.localeCompare(b.auctionName);
        }
      }
    });
  });

  first = signal(0);

  @Input() id!: number;

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData(
        {
          id: this.id!,
          pageName: "My Auctions",
          pageRoute: "my-auctions"
        }
      );
    },100);

    if (typeof window !== 'undefined') {
      window.onpageshow = function (event) {
        if (event.persisted) {
          window.location.reload();
        }
      };
      if (!this.isAuthenticated().isAuthenticated) {
        window.open(environment.mediaUrl, '_self');
        return;
      }else{
        this.getMyAuctionsList();
      }
    }

    this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
      if (isAuth) {
        this.getMyAuctionsList();
      } else {
        if (typeof window !== 'undefined') {
          window.open(environment.mediaUrl, '_self');
        }
        return;
      }
    });

  }

  onPageChange(event: any) {
    this.first.set(event.first);
  }

  getMyAuctionsList() {
    this.myAuctionsService.getMyAuctions().subscribe(({
      next: () => {
        this.myAuctionsList.update(auctions => this.myAuctionsService.myAuctionsListSignal())
        this.cdr.detectChanges(); 
      },
      error: (err) => {
        console.log('Error retrieve data', err);
      }
    }))
  }
}
